import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AccountPage } from '../features/account/AccountPage.tsx';
import { CampaignPage } from '../features/campaigns/CampaignPage.tsx';
import { CampaignSettingsPage } from '../features/campaigns/CampaignSettingsPage.tsx';
import { CampaignsPage } from '../features/campaigns/CampaignsPage.tsx';
import { ComplianceSettingsPage } from '../features/compliance/ComplianceSettingsPage.tsx';
import { SendingHoursPage } from '../features/compliance/SendingHoursPage.tsx';
import { SuppressionPage } from '../features/compliance/SuppressionPage.tsx';
import { SimulatorPage } from '../features/dev/SimulatorPage.tsx';
import { LoginPage } from '../features/auth/LoginPage.tsx';
import { WelcomePage } from '../features/auth/WelcomePage.tsx';
import { ImportDetailPage } from '../features/imports/ImportDetailPage.tsx';
import { ImportsPage } from '../features/imports/ImportsPage.tsx';
import { InboxPage } from '../features/inbox/InboxPage.tsx';
import { LeadSourcesPage } from '../features/intake/LeadSourcesPage.tsx';
import type { InboxFilters } from '../features/inbox/use-inbox.ts';
import { MembersPage } from '../features/members/MembersPage.tsx';
import { MerchantPage } from '../features/merchants/MerchantPage.tsx';
import { MerchantsPage } from '../features/merchants/MerchantsPage.tsx';
import { NewMerchantPage } from '../features/merchants/NewMerchantPage.tsx';
import { PlatformPage } from '../features/platform/PlatformPage.tsx';
import { PlatformWorkspacePage } from '../features/platform/PlatformWorkspacePage.tsx';
import { FieldsSettingsPage } from '../features/settings/FieldsSettingsPage.tsx';
import { NumbersSettingsPage } from '../features/settings/NumbersSettingsPage.tsx';
import { TagsSettingsPage } from '../features/settings/TagsSettingsPage.tsx';
import { WorkspaceSettingsPage } from '../features/settings/WorkspaceSettingsPage.tsx';
import type { AuthState } from '../lib/auth.tsx';
import { env } from '../lib/env.ts';
import { safeRedirect } from '../lib/redirects.ts';
import { AppLayout } from './AppLayout.tsx';
import { MorePage } from './MorePage.tsx';
import { AdminOnly, MemberOnly, NotFoundPage, PlatformOwnerOnly } from './guards.tsx';

export interface RouterContext {
  auth: AuthState;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional().catch(undefined) }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.status === 'signed_in')
      throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  validateSearch: z.object({ ws: z.uuid().optional().catch(undefined) }),
  component: WelcomePage,
});

/** Everything below requires a session; AppLayout picks the shell or the no-access screen. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (context.auth.status !== 'signed_in') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const claims = context.auth.claims;
    throw redirect({ to: !claims?.workspaceId && claims?.platformOwner ? '/platform' : '/inbox' });
  },
});

const inboxSearchSchema = z.object({
  view: z
    .enum(['all', 'unread', 'needs_reply', 'scheduled', 'problems', 'opted_out', 'do_not_contact'])
    .optional()
    .catch(undefined),
  rep: z.uuid().optional().catch(undefined),
  /** M9: conversations of one campaign (from the campaign's screen). */
  campaign: z.uuid().optional().catch(undefined),
});
type InboxSearch = z.infer<typeof inboxSearchSchema>;

function inboxFilters(search: InboxSearch): InboxFilters {
  return { view: search.view ?? 'all', rep: search.rep ?? null, campaign: search.campaign ?? null };
}

function inboxSearch(filters: InboxFilters): InboxSearch {
  return {
    view: filters.view === 'all' ? undefined : filters.view,
    rep: filters.rep ?? undefined,
    campaign: filters.campaign ?? undefined,
  };
}

const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox',
  validateSearch: inboxSearchSchema,
  component: function InboxRoute() {
    const search = inboxRoute.useSearch();
    const navigate = inboxRoute.useNavigate();
    return (
      <MemberOnly>
        <InboxPage
          filters={inboxFilters(search)}
          selectedId={null}
          onFiltersChange={(next) => void navigate({ search: inboxSearch(next), replace: true })}
        />
      </MemberOnly>
    );
  },
});

const conversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox/$merchantId',
  validateSearch: inboxSearchSchema,
  component: function ConversationRoute() {
    const { merchantId } = conversationRoute.useParams();
    const search = conversationRoute.useSearch();
    const navigate = conversationRoute.useNavigate();
    return (
      <MemberOnly>
        <InboxPage
          filters={inboxFilters(search)}
          selectedId={merchantId}
          onFiltersChange={(next) => void navigate({ search: inboxSearch(next), replace: true })}
        />
      </MemberOnly>
    );
  },
});

const merchantsSearchSchema = z.object({
  q: z.string().max(100).optional().catch(undefined),
  rep: z.uuid().optional().catch(undefined),
  tag: z.uuid().optional().catch(undefined),
  archived: z.boolean().optional().catch(undefined),
});

const merchantsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/merchants',
  validateSearch: merchantsSearchSchema,
  component: function MerchantsRoute() {
    const search = merchantsRoute.useSearch();
    const navigate = merchantsRoute.useNavigate();
    const filters = {
      q: search.q ?? '',
      rep: search.rep ?? null,
      tag: search.tag ?? null,
      archived: search.archived ?? false,
    };
    return (
      <MemberOnly>
        <MerchantsPage
          filters={filters}
          onFiltersChange={(next) =>
            void navigate({
              search: {
                q: next.q || undefined,
                rep: next.rep ?? undefined,
                tag: next.tag ?? undefined,
                archived: next.archived || undefined,
              },
              replace: true,
            })
          }
        />
      </MemberOnly>
    );
  },
});

const newMerchantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/merchants/new',
  component: () => (
    <MemberOnly>
      <NewMerchantPage />
    </MemberOnly>
  ),
});

/** M8: CSV imports (admins). */
const importsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/merchants/imports',
  component: () => (
    <AdminOnly>
      <ImportsPage />
    </AdminOnly>
  ),
});

const importRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/merchants/imports/$importId',
  component: function ImportRoute() {
    const { importId } = importRoute.useParams();
    return (
      <AdminOnly>
        <ImportDetailPage key={importId} importId={importId} />
      </AdminOnly>
    );
  },
});

const merchantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/merchants/$merchantId',
  component: function MerchantRoute() {
    const { merchantId } = merchantRoute.useParams();
    return (
      <MemberOnly>
        <MerchantPage key={merchantId} merchantId={merchantId} />
      </MemberOnly>
    );
  },
});

/** M9: campaigns (admins). */
const campaignsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns',
  component: () => (
    <AdminOnly>
      <CampaignsPage />
    </AdminOnly>
  ),
});

const campaignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/campaigns/$campaignId',
  component: function CampaignRoute() {
    const { campaignId } = campaignRoute.useParams();
    return (
      <AdminOnly>
        <CampaignPage key={campaignId} campaignId={campaignId} />
      </AdminOnly>
    );
  },
});

const campaignSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/campaigns',
  component: () => (
    <AdminOnly>
      <CampaignSettingsPage />
    </AdminOnly>
  ),
});

const fieldsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/fields',
  component: () => (
    <AdminOnly>
      <FieldsSettingsPage />
    </AdminOnly>
  ),
});

const tagsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/tags',
  component: () => (
    <AdminOnly>
      <TagsSettingsPage />
    </AdminOnly>
  ),
});

const numbersSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/numbers',
  component: () => (
    <AdminOnly>
      <NumbersSettingsPage />
    </AdminOnly>
  ),
});

const complianceSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/compliance',
  component: () => (
    <AdminOnly>
      <ComplianceSettingsPage />
    </AdminOnly>
  ),
});

const sendingHoursRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/sending-hours',
  component: () => (
    <AdminOnly>
      <SendingHoursPage />
    </AdminOnly>
  ),
});

/** M8: webhook lead sources and saved vendor columns (admins). */
const leadSourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/lead-sources',
  component: () => (
    <AdminOnly>
      <LeadSourcesPage />
    </AdminOnly>
  ),
});

const suppressionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/suppression',
  component: () => (
    <AdminOnly>
      <SuppressionPage />
    </AdminOnly>
  ),
});

/** Local development only (VITE_APP_ENV=local); the worker refuses it elsewhere anyway. */
const simulatorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dev/simulator',
  beforeLoad: () => {
    if (env().VITE_APP_ENV !== 'local') throw redirect({ to: '/inbox' });
  },
  component: () => (
    <AdminOnly>
      <SimulatorPage />
    </AdminOnly>
  ),
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/workspace',
  component: () => (
    <AdminOnly>
      <WorkspaceSettingsPage />
    </AdminOnly>
  ),
});

const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/members',
  component: () => (
    <AdminOnly>
      <MembersPage />
    </AdminOnly>
  ),
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});

/** Phone "More" tab: everything that is not a main tab (brand/layout.md "Mobile web"). */
const moreRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/more',
  component: MorePage,
});

const platformRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform',
  component: () => (
    <PlatformOwnerOnly>
      <PlatformPage />
    </PlatformOwnerOnly>
  ),
});

const platformWorkspaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/workspaces/$workspaceId',
  component: function PlatformWorkspaceRoute() {
    const { workspaceId } = platformWorkspaceRoute.useParams();
    return (
      <PlatformOwnerOnly>
        <PlatformWorkspacePage workspaceId={workspaceId} />
      </PlatformOwnerOnly>
    );
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  welcomeRoute,
  appRoute.addChildren([
    indexRoute,
    inboxRoute,
    conversationRoute,
    merchantsRoute,
    newMerchantRoute,
    importsRoute,
    importRoute,
    merchantRoute,
    campaignsRoute,
    campaignRoute,
    campaignSettingsRoute,
    workspaceSettingsRoute,
    fieldsSettingsRoute,
    tagsSettingsRoute,
    numbersSettingsRoute,
    complianceSettingsRoute,
    sendingHoursRoute,
    suppressionRoute,
    leadSourcesRoute,
    simulatorRoute,
    membersRoute,
    accountRoute,
    moreRoute,
    platformRoute,
    platformWorkspaceRoute,
  ]),
]);

export function createAppRouter(auth: AuthState) {
  return createRouter({ routeTree, context: { auth }, defaultPreload: false });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
